import { useMemo, useState } from "react";
import { X, Loader2, Send, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { createTemplate, deleteTemplate } from "../../services/templatesApi";
import TemplatePreview from "./TemplatePreview";
import ButtonsBuilder from "./ButtonsBuilder";

const CATEGORIES = [
  { value: "MARKETING", label: "Marketing" },
  { value: "UTILITY", label: "Utilitário" },
  { value: "AUTHENTICATION", label: "Autenticação" },
];

const LANGUAGES = [
  { value: "pt_BR", label: "Português (Brasil)" },
  { value: "pt_PT", label: "Português (Portugal)" },
  { value: "en", label: "Inglês" },
  { value: "en_US", label: "Inglês (US)" },
  { value: "es", label: "Espanhol" },
  { value: "es_AR", label: "Espanhol (AR)" },
  { value: "es_MX", label: "Espanhol (MX)" },
];

const NAME_REGEX = /^[a-z0-9_]{1,512}$/;

function countVars(text) {
  return (text?.match(/\{\{\d+\}\}/g) || []).length;
}

export default function TemplateEditor({ contaId, isDuplicate, source, onClose, onSaved }) {
  const seed = source && isDuplicate
    ? {
        name: `${source.name}_v2`,
        language: source.language,
        category: source.category,
        components: source.components || [],
      }
    : { name: "", language: "pt_BR", category: "MARKETING", components: [] };

  const [name, setName] = useState(seed.name);
  const [language, setLanguage] = useState(seed.language);
  const [category, setCategory] = useState(seed.category);
  const [headerText, setHeaderText] = useState(() => seed.components.find((c) => c.type === "HEADER")?.text || "");
  const [bodyText, setBodyText] = useState(() => seed.components.find((c) => c.type === "BODY")?.text || "");
  const [bodyExamples, setBodyExamples] = useState(() => {
    const ex = seed.components.find((c) => c.type === "BODY")?.example?.body_text?.[0];
    return Array.isArray(ex) ? ex.join(", ") : "";
  });
  const [footerText, setFooterText] = useState(() => seed.components.find((c) => c.type === "FOOTER")?.text || "");
  const [buttons, setButtons] = useState(() => {
    const b = seed.components.find((c) => c.type === "BUTTONS")?.buttons;
    return Array.isArray(b) ? b : [];
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const components = useMemo(() => {
    const out = [];
    if (headerText.trim()) out.push({ type: "HEADER", format: "TEXT", text: headerText.trim() });
    if (bodyText.trim()) {
      const block = { type: "BODY", text: bodyText };
      const varCount = countVars(bodyText);
      if (varCount > 0) {
        const arr = bodyExamples.split(",").map((s) => s.trim()).filter(Boolean).slice(0, varCount);
        if (arr.length === varCount) {
          block.example = { body_text: [arr] };
        }
      }
      out.push(block);
    }
    if (footerText.trim()) out.push({ type: "FOOTER", text: footerText.trim() });
    if (buttons.length > 0) out.push({ type: "BUTTONS", buttons });
    return out;
  }, [headerText, bodyText, bodyExamples, footerText, buttons]);

  const varCount = countVars(bodyText);
  const exampleArr = bodyExamples.split(",").map((s) => s.trim()).filter(Boolean);
  const examplesValid = varCount === 0 || exampleArr.length === varCount;

  const nameValid = NAME_REGEX.test(name);
  const hasBody = !!bodyText.trim();
  const canSubmit = nameValid && hasBody && examplesValid && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const saved = await createTemplate(contaId, { name, language, category, components });
      if (isDuplicate && source?.meta_template_id) {
        try {
          await deleteTemplate(contaId, source.meta_template_id);
        } catch (delErr) {
          toast.warn(`Novo template criado, mas falhou ao remover o antigo: ${delErr.response?.data?.message || delErr.message}`);
        }
      }
      toast.success("Template enviado pra aprovação da Meta (pode levar até 24h)");
      onSaved?.(saved);
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-surface-1 border border-white/[0.06] rounded-lg w-full max-w-5xl my-8 max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-white/[0.06] px-6 py-4">
          <h3 className="text-lg font-semibold text-white">
            {isDuplicate ? `Duplicar template "${source?.name}"` : "Novo template"}
          </h3>
          <button onClick={onClose} className="text-ink-4 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 overflow-y-auto">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-ink-3 block mb-1">Nome (snake_case)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_").slice(0, 512))}
                placeholder="boas_vindas"
                className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5 font-mono"
              />
              {!nameValid && name.length > 0 && (
                <p className="text-xs text-red-400 mt-1">Apenas a-z, 0-9 e _</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-ink-3 block mb-1">Idioma</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-3 block mb-1">Categoria</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-ink-3 block mb-1">Header (opcional, max 60)</label>
              <input
                type="text"
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value.slice(0, 60))}
                maxLength={60}
                placeholder="Bem-vindo!"
                className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-ink-3 block mb-1">
                Body (obrigatório, max 1024) — use {"{{1}}, {{2}}"} pra variáveis
              </label>
              <textarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value.slice(0, 1024))}
                rows={5}
                maxLength={1024}
                placeholder="Olá {{1}}, sua compra de R$ {{2}} foi aprovada!"
                className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5 resize-none"
              />
              <div className="text-xs text-ink-5 mt-1">{bodyText.length}/1024 caracteres</div>
            </div>

            {varCount > 0 && (
              <div>
                <label className="text-xs font-medium text-ink-3 block mb-1">
                  Exemplos das {varCount} variável(eis), separados por vírgula
                </label>
                <input
                  type="text"
                  value={bodyExamples}
                  onChange={(e) => setBodyExamples(e.target.value)}
                  placeholder="João, 199,90"
                  className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5"
                />
                {!examplesValid && (
                  <p className="text-xs text-red-400 mt-1">
                    A Meta exige {varCount} exemplo(s) — você forneceu {exampleArr.length}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-ink-3 block mb-1">Footer (opcional, max 60)</label>
              <input
                type="text"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value.slice(0, 60))}
                maxLength={60}
                placeholder="Equipe Metriqa"
                className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5"
              />
            </div>

            <ButtonsBuilder buttons={buttons} onChange={setButtons} />

            {error && (
              <div className="bg-red-950 border border-red-900 text-red-300 text-xs rounded p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <TemplatePreview components={components} />
            <div className="text-xs text-ink-4 px-1">
              Após enviar, a Meta avalia em até 24h. Você pode acompanhar o status na lista de templates.
            </div>
          </div>

          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-4 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-ink-2 hover:text-white px-4 py-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="bg-brand-600 hover:bg-brand-700 disabled:bg-surface-3 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submeter pra aprovação
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
